import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from 'components/ui/Button'
import { Modal } from 'components/ui/Modal'
import { Badge } from 'components/ui/Badge'
import {
  organizationsApi,
  type Organization
} from 'lib/api'
import { useToast } from 'contexts/ToastContext'
import { formatDate } from 'lib/utils'
import {
  Building2,
  Globe,
  Mail,
  Phone,
  MapPin,
  Calendar,
  CheckCircle2,
  XCircle,
  Loader2,
  Users,
  Briefcase,
  Link as LinkIcon
} from 'lucide-react'

interface OrganizationProfileModalProps {
  isOpen: boolean
  onClose: () => void
  organization: Organization | null
  onToggleActive?: (organization: Organization, newValue: boolean) => void
  onStatusChange?: (organization: Organization, newStatus: Organization['status']) => void
  updatingStatusOrgId?: number | null
}

export function OrganizationProfileModal({
  isOpen,
  onClose,
  organization,
  onToggleActive,
  onStatusChange,
  updatingStatusOrgId
}: OrganizationProfileModalProps) {
  const { t } = useTranslation()
  const { addToast } = useToast()
  const [orgDetails, setOrgDetails] = useState<Organization | null>(null)
  const [loading, setLoading] = useState(false)
  const [apiCost, setApiCost] = useState<number | null>(null)
  const [loadingApiCost, setLoadingApiCost] = useState(false)

  useEffect(() => {
    if (isOpen && organization) {
      fetchOrganizationDetails()
      fetchApiCost()
    } else {
      setOrgDetails(null)
      setApiCost(null)
    }
  }, [isOpen, organization?.id])

  const fetchOrganizationDetails = async () => {
    if (!organization) return

    try {
      setLoading(true)
      const response = await organizationsApi.getById(organization.id)
      setOrgDetails(response.data)
    } catch (error) {
      console.error('Error fetching organization details:', error)
      addToast({
        type: 'error',
        title: t('common.error'),
        message: t('organizations.messages.loadOrgError', 'Failed to load organization details'),
        duration: 5000
      })
    } finally {
      setLoading(false)
    }
  }

  const fetchApiCost = async () => {
    if (!organization) return

    try {
      setLoadingApiCost(true)
      const response = await organizationsApi.getApiCost(organization.id)
      if (response.data && response.data.api_usage !== undefined) {
        setApiCost(response.data.api_usage)
      }
    } catch (error) {
      console.error('Error fetching API cost:', error)
      // Don't show toast for API cost errors, silently fail
      setApiCost(null)
    } finally {
      setLoadingApiCost(false)
    }
  }

  if (!organization) return null

  // Merge orgDetails with organization prop, prioritizing organization prop for status
  const displayOrg = orgDetails
    ? {
      ...orgDetails,
      // Always use the latest status from organization prop (updated optimistically in parent)
      is_active: (organization as any).is_active !== false,
    }
    : organization

  const getLogoUrl = (logo?: string): string | null => {
    if (!logo) return null
    if (logo.startsWith('http://') || logo.startsWith('https://')) return logo
    const envUrl = process.env.REACT_APP_API_URL
    const baseUrl = envUrl && envUrl.trim().length > 0
      ? envUrl.trim().replace(/\/+$/, '')
      : `http://${window.location.hostname}:8000`
    // Media files are served from /media/, not /api/media/
    const logoPath = logo.startsWith('/') ? logo : `/${logo}`
    return `${baseUrl}/media${logoPath}`
  }

  const footer = (
    <div className="flex justify-end space-x-3">
      <Button
        variant="outline"
        onClick={onClose}
      >
        {t('common.close', 'Close')}
      </Button>
    </div>
  )

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('organizations.profile', 'Organization Profile')}
      footer={footer}
    >
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Organization Header */}
          <div className="flex items-start space-x-4 pb-6 border-b">
            <div className="h-20 w-20 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 overflow-hidden">
              {getLogoUrl(displayOrg.logo) ? (
                <img
                  src={getLogoUrl(displayOrg.logo)!}
                  alt={displayOrg.name}
                  className="h-20 w-20 rounded-full object-cover"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none'
                  }}
                />
              ) : (
                <Building2 className="h-10 w-10 text-primary" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-2xl font-bold text-foreground truncate">
                {displayOrg.name}
              </h2>
              {displayOrg.domain && (
                <p className="text-muted-foreground flex items-center gap-2 mt-1">
                  <Globe className="h-4 w-4" />
                  {displayOrg.domain}
                </p>
              )}
              <div className="flex items-center gap-3 mt-3 flex-wrap">
                {/* Status Badge */}
                <Badge 
                  className={
                    displayOrg.status === 'suspended' 
                      ? 'bg-yellow-500 text-white hover:bg-yellow-600 border-transparent'
                      : displayOrg.status === 'cancelled'
                      ? 'bg-orange-500 text-white hover:bg-orange-600 border-transparent'
                      : displayOrg.status === 'payment_failed'
                      ? 'bg-red-500 text-white hover:bg-red-600 border-transparent'
                      : displayOrg.status === 'terminated'
                      ? 'bg-gray-500 text-white hover:bg-gray-600 border-transparent'
                      : displayOrg.is_active !== false
                      ? 'bg-green-500 text-white hover:bg-green-600 border-transparent'
                      : 'bg-red-500 text-white hover:bg-red-600 border-transparent'
                  }
                >
                  {displayOrg.status === 'suspended'
                    ? t('organizations.status.suspended', 'Suspended')
                    : displayOrg.status === 'cancelled'
                    ? t('organizations.status.cancelled', 'Cancelled')
                    : displayOrg.status === 'payment_failed'
                    ? t('organizations.status.paymentFailed', 'Payment Failed')
                    : displayOrg.status === 'terminated'
                    ? t('organizations.status.terminated', 'Terminated')
                    : displayOrg.is_active !== false
                    ? t('organizations.status.active', 'Active')
                    : t('organizations.status.inactive', 'Inactive')
                  }
                </Badge>

                {/* Status Change Dropdown */}
                {onStatusChange && organization && (
                  <select
                    value={displayOrg.status || 'active'}
                    onChange={(e) => {
                      const newStatus = e.target.value as Organization['status']
                      if (newStatus && onStatusChange) {
                        onStatusChange(organization, newStatus)
                      }
                    }}
                    disabled={updatingStatusOrgId === organization.id}
                    className="px-3 py-1.5 text-sm border rounded-md bg-background text-foreground disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="active">{t('organizations.status.active', 'Active')}</option>
                    <option value="suspended">{t('organizations.status.suspended', 'Suspended')}</option>
                    <option value="cancelled">{t('organizations.status.cancelled', 'Cancelled')}</option>
                    <option value="payment_failed">{t('organizations.status.paymentFailed', 'Payment Failed')}</option>
                    <option value="terminated">{t('organizations.status.terminated', 'Terminated')}</option>
                  </select>
                )}

                {/* Quick Toggle Active/Inactive */}
                {onToggleActive && organization && (
                  <div className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 relative transition-all duration-200`}>
                    {!displayOrg.is_active && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onToggleActive(organization, true)}
                        disabled={updatingStatusOrgId === organization.id}
                        title={t('organizations.activateOrg', 'Activate organization')}
                        className="h-6 w-6 p-0 bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-300 dark:hover:bg-green-900/50 rounded-full transition-all duration-200"
                        aria-label={t('organizations.activateOrg', 'Activate organization')}
                      >
                        {updatingStatusOrgId === organization.id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <CheckCircle2 className="h-3 w-3" />
                        )}
                      </Button>
                    )}
                    <span
                      className={`h-2.5 w-2.5 rounded-full transition-colors duration-200 ${displayOrg.is_active
                          ? 'bg-green-500 dark:bg-green-400'
                          : 'bg-red-500 dark:bg-red-400'
                        }`}
                    ></span>
                    <span className={`text-xs transition-colors duration-200`}>
                      {displayOrg.is_active ? t('common.active') : t('common.inactive')}
                    </span>
                    {displayOrg.is_active && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onToggleActive(organization, false)}
                        disabled={updatingStatusOrgId === organization.id}
                        title={t('organizations.deactivateOrg', 'Deactivate organization')}
                        className="h-6 w-6 p-0 bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-300 dark:hover:bg-red-900/50 rounded-full transition-all duration-200"
                        aria-label={t('organizations.deactivateOrg', 'Deactivate organization')}
                      >
                        {updatingStatusOrgId === organization.id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <XCircle className="h-3 w-3" />
                        )}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Organization Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Basic & Contact Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-foreground">
                {t('organizations.basicInformation', 'Basic Information')}
              </h3>

              <div className="space-y-3">
                {displayOrg.description && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      {t('organizations.form.description', 'Description')}
                    </label>
                    <p className="mt-1 text-foreground">{displayOrg.description}</p>
                  </div>
                )}

                {displayOrg.website && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                      <LinkIcon className="h-4 w-4" />
                      {t('organizations.form.website', 'Website')}
                    </label>
                    <p className="mt-1 text-foreground">
                      <a href={displayOrg.website} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                        {displayOrg.website}
                      </a>
                    </p>
                  </div>
                )}

                {displayOrg.email && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      {t('organizations.form.email', 'Email')}
                    </label>
                    <p className="mt-1 text-foreground">{displayOrg.email}</p>
                  </div>
                )}

                {displayOrg.phone && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      {t('organizations.form.phone', 'Phone')}
                    </label>
                    <p className="mt-1 text-foreground">{displayOrg.phone}</p>
                  </div>
                )}

                {displayOrg.address && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      {t('organizations.form.address', 'Address')}
                    </label>
                    <p className="mt-1 text-foreground">{displayOrg.address}</p>
                  </div>
                )}

                {displayOrg.created_at && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      {t('organizations.created', 'Created')}
                    </label>
                    <p className="mt-1 text-foreground">
                      {formatDate(displayOrg.created_at)}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Subscription & Limits */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-foreground">
                {t('organizations.subscriptionAndLimits', 'Subscription & Limits')}
              </h3>

              <div className="space-y-3">
                {displayOrg.subscription_plan && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                      <Briefcase className="h-4 w-4" />
                      {t('organizations.form.subscriptionPlan', 'Subscription Plan')}
                    </label>
                    <p className="mt-1">
                      <Badge
                        variant="default"
                        className={
                          displayOrg.subscription_plan.toLowerCase() === 'starter'
                            ? 'bg-blue-500 text-white hover:bg-blue-600 border-transparent'
                            : displayOrg.subscription_plan.toLowerCase() === 'premium'
                              ? 'bg-purple-500 text-white hover:bg-purple-600 border-transparent'
                              : displayOrg.subscription_plan.toLowerCase() === 'advanced'
                                ? 'bg-orange-500 text-white hover:bg-orange-600 border-transparent'
                                : ''
                        }
                      >
                        {displayOrg.subscription_plan.charAt(0).toUpperCase() + displayOrg.subscription_plan.slice(1)}
                      </Badge>
                    </p>
                  </div>
                )}

                {displayOrg.subscription_deadline && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      {t('organizations.deadline', 'Subscription Deadline')}
                    </label>
                    <p className="mt-1 text-foreground">{formatDate(displayOrg.subscription_deadline)}</p>
                  </div>
                )}

                {displayOrg.subscription_duration_days && displayOrg.subscription_duration_days > 0 && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      {t('organizations.duration', 'Subscription Duration')}
                    </label>
                    <p className="mt-1 text-foreground">
                      {displayOrg.subscription_duration_days} {t('organizations.days', 'days')}
                    </p>
                  </div>
                )}

                {displayOrg.number_of_users !== undefined && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      {t('organizations.form.numberOfUsers', 'Number of Users')}
                    </label>
                    <p className="mt-1 text-foreground">{displayOrg.number_of_users}</p>
                  </div>
                )}

                {displayOrg.daily_cv_processing_limit !== undefined && displayOrg.daily_cv_processing_limit > 0 && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                      <Briefcase className="h-4 w-4" />
                      {t('organizations.form.dailyCvLimit', 'Daily CV Processing Limit')}
                    </label>
                    <p className="mt-1 text-foreground">{displayOrg.daily_cv_processing_limit}</p>
                  </div>
                )}

                {displayOrg.daily_job_offer_limit !== undefined && displayOrg.daily_job_offer_limit > 0 && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                      <Briefcase className="h-4 w-4" />
                      {t('organizations.form.dailyOfferLimit', 'Daily Job Offer Limit')}
                    </label>
                    <p className="mt-1 text-foreground">{displayOrg.daily_job_offer_limit}</p>
                  </div>
                )}

                {(!displayOrg.subscription_plan &&
                  !displayOrg.subscription_deadline &&
                  (!displayOrg.subscription_duration_days || displayOrg.subscription_duration_days === 0) &&
                  displayOrg.number_of_users === undefined &&
                  (!displayOrg.daily_cv_processing_limit || displayOrg.daily_cv_processing_limit === 0) &&
                  (!displayOrg.daily_job_offer_limit || displayOrg.daily_job_offer_limit === 0)) && (
                    <div className="text-sm text-muted-foreground italic">
                      {t('organizations.noSubscriptionInfo', 'No subscription information available')}
                    </div>
                  )}
              </div>
            </div>
          </div>

          {/* Consumption & Usage */}
          {displayOrg.consumption && (
            <div className="pt-6 border-t space-y-4">
              <h3 className="text-xl font-bold text-foreground">
                {t('organizations.consumptionTitle', "Consommation de l'Organisation")}
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Candidates Consumption */}
                <div className="p-4 rounded-lg bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-100 dark:border-emerald-900/30 space-y-3">
                  <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400 font-bold mb-2">
                    <Users className="h-5 w-5" />
                    <span>{t('organizations.candidatesUsage', 'Candidats')} ({displayOrg.consumption.candidates_count})</span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-muted-foreground">{t('organizations.cost', 'Coût total')}:</span>
                      <span className="font-semibold">{displayOrg.consumption.total_candidate_cost.toFixed(8)} $</span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-muted-foreground">{t('organizations.tokensInput', 'Tokens Entrée')}:</span>
                      <span className="font-semibold">{displayOrg.consumption.total_candidate_input_tokens.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-muted-foreground">{t('organizations.tokensOutput', 'Tokens Sortie')}:</span>
                      <span className="font-semibold">{displayOrg.consumption.total_candidate_output_tokens.toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                {/* Offers Consumption */}
                <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-950/20 border border-blue-100 dark:border-blue-900/30 space-y-3">
                  <div className="flex items-center gap-2 text-blue-700 dark:text-blue-400 font-bold mb-2">
                    <Briefcase className="h-5 w-5" />
                    <span>{t('organizations.offersUsage', 'Offres')} ({displayOrg.consumption.offers_count})</span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-muted-foreground">{t('organizations.cost', 'Coût total')}:</span>
                      <span className="font-semibold">{displayOrg.consumption.total_offer_cost.toFixed(8)} $</span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-muted-foreground">{t('organizations.tokensInput', 'Tokens Entrée')}:</span>
                      <span className="font-semibold">{displayOrg.consumption.total_offer_input_tokens.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-muted-foreground">{t('organizations.tokensOutput', 'Tokens Sortie')}:</span>
                      <span className="font-semibold">{displayOrg.consumption.total_offer_output_tokens.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-4 rounded-lg bg-slate-100 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 flex justify-between items-center">
                <span className="text-lg font-bold">{t('organizations.totalCost', 'Coût Total de l\'Organisation')}:</span>
                <div className="flex flex-col items-end gap-2">
                  <span className="text-2xl font-black text-primary">{displayOrg.consumption.total_cost.toFixed(8)} $</span>
                  {apiCost !== null && (
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-muted-foreground">API Cost:</span>
                      {loadingApiCost ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <span className="font-semibold text-amber-600 dark:text-amber-400">${apiCost.toFixed(8)}</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  )
}

